import classNames from 'classnames';
import FeatureFlagModule, {
  FeatureFlagKeys,
  useFeatureFlags,
} from '@modules/FeatureFlagModule';
import { Link, useLocation } from 'react-router-dom';

import NavBarLogo from '@assets/chrome/navbar-logo.svg';
import ManagerAssistIcon from '@components/ManagerAssist/ManagerAssistIcon';
import ContactCenterTitle from '@assets/contact-center-title.svg';
import paths from '@app/routes/paths';
import useAuth from '@modules/AuthModule/hooks/useAuth';
import { BodyText, Icon, Stack, Tag } from '@servicetitan/design-system';
import { Button, Tooltip } from '@servicetitan/anvil2';
import RefreshIcon from '@servicetitan/anvil2/assets/icons/material/round/refresh.svg';
import styles from './NavBar.module.css';
import UserMenu from './components/UserMenu/UserMenu';
import { permissionSets, RequirePermissions } from '@app/permissions';
import noop from '@lib/utils/noop';
import { UserStatusMenu } from '@modules/UserStatusModule/components/UserStatusMenu';
import { mainLayoutLocators } from '@locators/main-layout';
import Announcements from '@modules/AnnouncementsModule/components/Announcements';
import MicrophoneAccessModal from '@modules/BrowserModule/components/MicrophoneAccessModal';
import { HelpCenterButton } from '@servicetitan/help-center';
import {
  FeatureErrorBoundary,
  ModuleErrorBoundary,
} from '@components/ErrorBoundary';
import RequireFeature from '@app/routes/RequireFeature';
import CallContinuityBanner from '@components/CallContinuityBanner/CallContinuityBanner';
import { useRef } from 'react';
import { strings } from './strings';
import ModalsModule from '@modules/ModalsModule';
import { ModalId } from '@modules/ModalsModule/types';

type NavBarProps = {
  onToggleDebug?: () => void;
  onToggleIsManagerAssistActive?: () => void;
  isManagerAssistActive?: boolean;
};

const NavBar = ({
  onToggleDebug = noop,
  isManagerAssistActive = false,
  onToggleIsManagerAssistActive = noop,
}: NavBarProps) => {
  const { serviceName } = useAuth();
  const { pathname } = useLocation();
  const {
    [FeatureFlagKeys.featureHelpCenter]: featureHelpCenter,
    [FeatureFlagKeys.featureMonitorAndResetUserStatus]:
      featureMonitorAndResetUserStatus,
  } = useFeatureFlags();
  const { user } = useAuth();
  const resetAccountButtonRef = useRef<HTMLButtonElement>(null);

  const isSettings = pathname.startsWith(`/settings`);

  const handleResetUserStatusModalOpen = () => {
    ModalsModule.openModal(ModalId.ResetAccountModal, {
      open: true,
      onClose: () => ModalsModule.closeActiveModal(),
    });
    // Blur the button to hide the tooltip
    resetAccountButtonRef.current?.blur();
  };

  const handleNavLogoClick = () => {
    onToggleDebug();
  };

  const handleManagerAssistIconClick = () => {
    onToggleIsManagerAssistActive();
  };

  const isManagerAssistEnabled = FeatureFlagModule.isEnabled(
    FeatureFlagKeys.featureManagerAssist,
  );

  return (
    <>
      <ModuleErrorBoundary moduleName='Announcements' size='compact'>
        <Announcements />
      </ModuleErrorBoundary>
      <RequireFeature flagName={FeatureFlagKeys.featureCallContinuityBanner}>
        <FeatureErrorBoundary featureName='Call Continuity Banner'>
          <CallContinuityBanner />
        </FeatureErrorBoundary>
      </RequireFeature>
      <ModuleErrorBoundary moduleName='MicrophoneAccess' size='compact'>
        <MicrophoneAccessModal />
      </ModuleErrorBoundary>
      <Stack
        className={classNames('bg-neutral-300', styles.navBarContainer)}
        justifyContent='space-between'
      >
        <Stack>
          <ModuleErrorBoundary moduleName='NavBarLogo' size='compact'>
            <Stack
              id={mainLayoutLocators.navBar.logo}
              onClick={handleNavLogoClick}
              className={classNames('bg-white', styles.logoContainer)}
              justifyContent='center'
              alignItems='center'
            >
              <NavBarLogo />
            </Stack>
            <Link to={paths.home} className='d-f m-l-1 align-items-center'>
              <ContactCenterTitle id={mainLayoutLocators.navBar.title} />
            </Link>
          </ModuleErrorBoundary>
          {user?.isImpersonated && (
            <div className={classNames('d-f', 'align-items-center', 'm-l-2')}>
              <Tag className={classNames('m-l-1')} color='red'>
                Impersonating {user.name.toUpperCase()}
              </Tag>
            </div>
          )}
        </Stack>
        <Stack justifyContent='center' alignItems='center' spacing={3}>
          <BodyText className={classNames('c-neutral-70', 'ta-center')}>
            {serviceName}
          </BodyText>

          {featureMonitorAndResetUserStatus && (
            <Tooltip placement='top'>
              <Tooltip.Trigger className='m-0-i'>
                <Button
                  ref={resetAccountButtonRef}
                  appearance='ghost'
                  icon={RefreshIcon}
                  className='c-neutral-0'
                  onClick={handleResetUserStatusModalOpen}
                  id={mainLayoutLocators.navBar.resetAccountBtn}
                >
                  {strings.resetAccountButton}
                </Button>
              </Tooltip.Trigger>
              <Tooltip.Content>
                {strings.resetAccountButtonTooltip}
              </Tooltip.Content>
            </Tooltip>
          )}

          {isManagerAssistEnabled && (
            <RequirePermissions
              permissions={permissionSets.manager.MANAGER_ASSIST}
              permissionsMatch='all'
            >
              <ModuleErrorBoundary
                moduleName='ManagerAssistIcon'
                size='compact'
              >
                <Link
                  id={mainLayoutLocators.navBar.managerAssistBtn}
                  onClick={handleManagerAssistIconClick}
                  to={window.location.pathname}
                  className={styles.icon}
                >
                  <ManagerAssistIcon
                    size={24}
                    isActive={isManagerAssistActive}
                  />
                </Link>
              </ModuleErrorBoundary>
            </RequirePermissions>
          )}
          {featureHelpCenter && (
            <ModuleErrorBoundary moduleName='HelpCenter' size='compact'>
              <Link
                id={mainLayoutLocators.navBar.helpCenterBtn}
                to={window.location.pathname}
                className={styles.icon}
              >
                <HelpCenterButton />
              </Link>
            </ModuleErrorBoundary>
          )}
          <RequirePermissions
            permissions={permissionSets.admin.ALL}
            permissionsMatch='any'
          >
            <Link
              id={mainLayoutLocators.navBar.settingsBtn}
              className={classNames(
                styles.icon,
                isSettings && styles.activeIcon,
              )}
              to={paths.settings}
            >
              <Icon name='settings' size={24} />
            </Link>
          </RequirePermissions>

          <ModuleErrorBoundary moduleName='UserStatus' size='compact'>
            <UserStatusMenu />
          </ModuleErrorBoundary>
          <ModuleErrorBoundary moduleName='UserMenu' size='compact'>
            <UserMenu />
          </ModuleErrorBoundary>
        </Stack>
      </Stack>
    </>
  );
};

export default NavBar;
